// Shared analytics helpers for the Executive Dashboard (and future Chad/TL
// views). No external deps. Client-side aggregations over ~1k deals are fast
// and avoid complex RLS on server-side aggregate RPCs.

import type { Deal } from "./types";

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/**
 * Count formatter for crew metrics, which are fractional by design — a split
 * deal credits 0.5 to each rep. Integers render clean ("3"), halves keep the
 * decimal ("3.5").
 */
export function fmtCount(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? n.toString() : n.toFixed(1).replace(/\.0$/, "");
}

export function fmtUSD(n: number | null | undefined, opts?: { short?: boolean }): string {
  if (n == null || !Number.isFinite(n)) return "—";
  if (opts?.short) {
    if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${Math.round(n)}`;
  }
  // Always show 2 decimals on full dollar amounts (May 7, 2026 — per Greg).
  // Compact `short` mode is intentionally untouched (KPI tiles like "$1.2M").
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function fmtPct(n: number | null | undefined, digits = 0): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

export function fmtInt(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("en-US");
}

// ---------------------------------------------------------------------------
// Revenue recognition helpers
// ---------------------------------------------------------------------------

/** Per-deal revenue: prefer final_invoice_amount, fall back to
 *  commissionable_revenue. Returns 0 if neither set. Zoho migration populated
 *  these inconsistently — older deals often have only one of the two. */
export function dealRevenue(d: Pick<Deal, "final_invoice_amount" | "commissionable_revenue"> & {
  final_invoice_amount?: number | null;
  commissionable_revenue?: number | null;
}): number {
  return Number(d.final_invoice_amount) || Number(d.commissionable_revenue) || 0;
}

// ---------------------------------------------------------------------------
// Time windows
// ---------------------------------------------------------------------------

export type TimeWindow = "week" | "month30" | "ytd" | "year2025";

export function windowStart(w: TimeWindow): Date {
  const now = new Date();
  if (w === "week") {
    // Monday of this week (local time). RMs' "this week" is Mon–Sun.
    const day = now.getDay(); // 0=Sun .. 6=Sat
    const offset = (day + 6) % 7; // days since Monday
    const monday = new Date(now);
    monday.setDate(now.getDate() - offset);
    monday.setHours(0, 0, 0, 0);
    return monday;
  }
  if (w === "month30") {
    const d = new Date(now);
    d.setDate(d.getDate() - 30);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  if (w === "ytd") {
    return new Date(now.getFullYear(), 0, 1);
  }
  // year2025 — explicit calendar year for comparison baseline
  return new Date(2025, 0, 1);
}

export function windowEnd(w: TimeWindow): Date {
  if (w === "year2025") return new Date(2026, 0, 1);
  const now = new Date();
  now.setHours(23, 59, 59, 999);
  return now;
}

/** Days elapsed in the current window (for pacing calcs). */
export function daysInto(w: TimeWindow): number {
  const start = windowStart(w);
  const now = new Date();
  return Math.max(1, Math.ceil((now.getTime() - start.getTime()) / 86_400_000));
}

/** Total days in the window (365/366 for YTD, 30 for 30d, etc.). */
export function daysInWindow(w: TimeWindow): number {
  if (w === "week") return 7;
  if (w === "month30") return 30;
  if (w === "ytd") {
    const year = new Date().getFullYear();
    const start = new Date(year, 0, 1);
    const end = new Date(year + 1, 0, 1);
    return Math.round((end.getTime() - start.getTime()) / 86_400_000);
  }
  return 365;
}

// ---------------------------------------------------------------------------
// MVP scoring (per CLAUDE.md spec)
// Weighted composite: Claims 25%, Revenue 25%, Builds 20%, Appointments 15%,
// Avg Deal 15%. Split deals: claims/builds/appointments 0.5 each, revenue
// 50/50, but avg_deal uses FULL value (rewards pursuing bigger jobs).
// ---------------------------------------------------------------------------

export interface ScoreInputs {
  claims: number;
  revenue: number;
  builds: number;
  appointments: number;
  avgDeal: number;
}

export interface RankedRep {
  repId: string;
  repName: string;
  score: number;
  inputs: ScoreInputs;
}

/** Normalize each input to [0,1] across the cohort, then weighted sum. */
export function rankReps(reps: Array<{ repId: string; repName: string; inputs: ScoreInputs }>): RankedRep[] {
  if (reps.length === 0) return [];
  const maxClaims = Math.max(...reps.map((r) => r.inputs.claims), 1);
  const maxRevenue = Math.max(...reps.map((r) => r.inputs.revenue), 1);
  const maxBuilds = Math.max(...reps.map((r) => r.inputs.builds), 1);
  const maxAppts = Math.max(...reps.map((r) => r.inputs.appointments), 1);
  const maxAvg = Math.max(...reps.map((r) => r.inputs.avgDeal), 1);

  return reps
    .map((r) => {
      const score =
        0.25 * (r.inputs.claims / maxClaims) +
        0.25 * (r.inputs.revenue / maxRevenue) +
        0.2 * (r.inputs.builds / maxBuilds) +
        0.15 * (r.inputs.appointments / maxAppts) +
        0.15 * (r.inputs.avgDeal / maxAvg);
      return { ...r, score };
    })
    .sort((a, b) => b.score - a.score);
}

// ---------------------------------------------------------------------------
// Phase grouping (matches the 6-phase architecture)
// ---------------------------------------------------------------------------

export const PHASE_ORDER = [
  "drone",
  "leads",
  "claims",
  "reinspections",
  "builds",
  "closing",
] as const;

export const PHASE_LABEL: Record<string, string> = {
  drone: "Drone",
  leads: "Leads",
  claims: "Claims",
  reinspections: "Reinspections",
  builds: "Builds",
  closing: "Closing",
  prospect: "Leads", // legacy fallback
};
